import { useId, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';

import { formatDetectedAt } from './formatDetectedAt';
import type { RunConfirmation } from './useRunConfirmation';

type RunConfirmationContentProps = { confirmation: RunConfirmation };

function RunConfirmationTitle({ confirmation }: RunConfirmationContentProps): JSX.Element {
  return <>{confirmation.kind === 'no-new-data' ? 'No new data' : 'Detection ran recently'}</>;
}

function RunConfirmationBody({ confirmation }: RunConfirmationContentProps): JSX.Element {
  if (confirmation.kind === 'no-new-data') {
    return (
      <p className="mb-0">
        Detection found no new data since the last run. Running anyway judges the last data again and writes only what
        changed.
      </p>
    );
  }

  return (
    <>
      <p>The last run judged data up to {formatDetectedAt(confirmation.lastWatermark)}.</p>
      <p className="mb-0">The next regular run may start after {formatDetectedAt(confirmation.nextRunAfter)}.</p>
    </>
  );
}

type RunConfirmationDialogProps = {
  confirmation: RunConfirmation | undefined;
  onConfirm: () => void;
  onCancel: () => void;
};

function RunConfirmationDialog({ confirmation, onConfirm, onCancel }: RunConfirmationDialogProps): JSX.Element {
  const titleId = useId();
  const [shown, setShown] = useState(confirmation);
  if (confirmation !== undefined && confirmation !== shown) {
    setShown(confirmation);
  }

  return (
    <Modal show={confirmation !== undefined} onHide={onCancel} aria-labelledby={titleId}>
      <Modal.Header closeButton={true}>
        <Modal.Title id={titleId}>{shown !== undefined && <RunConfirmationTitle confirmation={shown} />}</Modal.Title>
      </Modal.Header>
      {shown !== undefined && (
        <Modal.Body>
          <RunConfirmationBody confirmation={shown} />
        </Modal.Body>
      )}
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onConfirm}>
          Run anyway
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RunConfirmationDialog;
